import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Play, Pause, SkipBack, SkipForward, ChevronDown, ChevronUp, SlidersHorizontal, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PassageDateDisplay } from './PassageDateDisplay';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

// Memoized component for era label content to prevent unnecessary re-renders
const EraLabelContent = memo(({ era, isHovered, formatYear }: {
    era: TimelineEra;
    isHovered: boolean;
    formatYear: (year: number) => string;
}) => (
    <div className="flex flex-col items-start">
        <span className={`text-[11px] md:text-xs font-medium ${isHovered ? 'text-foreground' : 'text-muted-foreground'}`}>
            {era.label}
        </span>
        <span className="text-[9px] md:text-[10px] text-muted-foreground/70">
            {formatYear(era.startYear)} - {formatYear(era.endYear)}
        </span>
    </div>
));

EraLabelContent.displayName = 'EraLabelContent';

interface TimelineSliderProps {
    minYear: number;
    maxYear: number;
    currentYear: number;
    eras: TimelineEra[];
    onYearChange: (year: number) => void;
    className?: string;
    passageDate?: DateProposal;
    alternativeDates?: DateProposal[];
}

export function TimelineSlider({
    minYear,
    maxYear,
    currentYear,
    eras,
    onYearChange,
    className = '',
    passageDate,
    alternativeDates
}: TimelineSliderProps) {
    const isDarkMode = useDarkMode();
    const [showDetails, setShowDetails] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showErasLegend, setShowErasLegend] = useState(false);
    const [hoveredEraIndex, setHoveredEraIndex] = useState<number | null>(null);
    const playSpeed = 50;

    // Auto-play functionality
    useEffect(() => {
        if (!isPlaying) return;

        const interval = setInterval(() => {
            const next = currentYear + Math.ceil(playSpeed / 10);
            if (next >= maxYear) {
                setIsPlaying(false);
                onYearChange(maxYear);
            }
            else {
                onYearChange(next);
            }
        }, 100); // Update every 100ms

        return () => clearInterval(interval);
    }, [isPlaying, playSpeed, maxYear, currentYear, onYearChange]);

    const handlePlayPause = useCallback(() => {
        setIsPlaying(prev => !prev);
    }, []);

    const handleStepBack = useCallback(() => {
        const step = Math.ceil((maxYear - minYear) / 100);
        onYearChange(Math.max(minYear, currentYear - step));
    }, [currentYear, minYear, maxYear, onYearChange]);

    const handleStepForward = useCallback(() => {
        const step = Math.ceil((maxYear - minYear) / 100);
        onYearChange(Math.min(maxYear, currentYear + step));
    }, [currentYear, minYear, maxYear, onYearChange]);

    const formatYear = useCallback((year: number): string => {
        if (year < 0) {
            return `${Math.abs(year)} BC`;
        }
        return `${year} AD`;
    }, []);

    const currentEra = useMemo(() => {
        return eras.find(era => currentYear >= era.startYear && currentYear <= era.endYear);
    }, [eras, currentYear]);

    // Calculate slider position (0-100)
    const yearToPercent = useCallback((year: number): number => {
        return ((year - minYear) / (maxYear - minYear)) * 100;
    }, [minYear, maxYear]);

    // Memoize era band calculations
    const eraBands = useMemo(() => {
        return eras.map((era, index) => {
            const left = yearToPercent(Math.max(era.startYear, minYear));
            const right = yearToPercent(Math.min(era.endYear, maxYear));
            const width = right - left;
            return { era, index, left, width };
        });
    }, [eras, minYear, maxYear, yearToPercent]);

    // Memoize formatted year strings
    const formattedCurrentYear = useMemo(() => formatYear(currentYear), [currentYear, formatYear]);
    const formattedMinYear = useMemo(() => formatYear(minYear), [minYear, formatYear]);
    const formattedMaxYear = useMemo(() => formatYear(maxYear), [maxYear, formatYear]);
    const currentYearPercent = useMemo(() => yearToPercent(currentYear), [currentYear, yearToPercent]);

    return (
        <div className={`relative bg-card border-t border-border pt-0 pb-3 px-3 md:pb-4 md:px-4 ${className}`}>
            {showDetails && (
                <div className="space-y-4 mb-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {/* Combined Era Chart + Slider */}
                    <div
                        className="relative cursor-pointer px-1"
                        onClick={(e) => {
                            // Calculate clicked position and convert to year
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const percent = (x / rect.width) * 100;
                            const year = Math.round(minYear + (percent / 100) * (maxYear - minYear));
                            onYearChange(Math.max(minYear, Math.min(maxYear, year)));
                        }}
                    >
                        {/* Era bands background */}
                        <div className="relative h-6 md:h-8 bg-muted rounded-lg overflow-hidden">
                            {eraBands.map(({ era, index, left, width }) => {
                                const isHovered = hoveredEraIndex === index;

                                return (
                                    <div
                                        key={index}
                                        className="absolute h-full transition-all"
                                        style={{
                                            left: `${left}%`,
                                            width: `${width}%`,
                                            backgroundColor: era.color,
                                            opacity: isHovered ? 0.9 : 0.6
                                        }}
                                        onMouseEnter={() => setHoveredEraIndex(index)}
                                        onMouseLeave={() => setHoveredEraIndex(null)}
                                    />
                                );
                            })}

                            {/* Current position indicator */}
                            <div
                                className="absolute top-0 bottom-0 w-0.5 bg-foreground shadow-lg pointer-events-none z-30"
                                style={{ left: `${currentYearPercent}%` }}
                            >
                                {/* Thumb/handle */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-foreground border-2 border-background rounded-full shadow-md" />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Era labels - Hide on mobile unless toggled */}
                        <div className={`space-y-1 md:space-y-2 ${showErasLegend ? 'block' : 'hidden md:block'}`}>
                            <div className="flex items-center gap-1 md:gap-2 flex-wrap text-xs">
                                {eras.map((era, index) => {
                                    const isHovered = hoveredEraIndex === index;

                                    return (
                                        <button
                                            key={index}
                                            onClick={() => onYearChange(era.startYear)}
                                            onMouseEnter={() => setHoveredEraIndex(index)}
                                            onMouseLeave={() => setHoveredEraIndex(null)}
                                            className={`flex items-center gap-1.5 md:gap-2 px-2 py-1.5 md:px-3 md:py-2 rounded transition-all flex ${isHovered
                                                ? 'bg-muted scale-105'
                                                : 'hover:bg-muted'
                                                }`}
                                            style={isHovered ? {
                                                boxShadow: `0 0 0 2px ${era.color}`,
                                                borderColor: era.color
                                            } : {}}
                                            title={`Jump to ${era.label}`}
                                        >
                                            <div
                                                className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: era.color }}
                                            />
                                            <EraLabelContent
                                                era={era}
                                                isHovered={isHovered}
                                                formatYear={formatYear}
                                            />
                                        </button>
                                    );
                                })}
                                {/* Year range - hide on mobile */}
                                <div className="hidden md:block text-xs text-muted-foreground ml-auto pr-24">
                                    {formattedMinYear} - {formattedMaxYear}
                                </div>
                            </div>
                        </div>

                        {/* Play controls */}
                        <div className="flex items-center justify-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={handleStepBack}
                                    disabled={currentYear <= minYear}
                                    className="h-8 w-8"
                                >
                                    <SkipBack className="h-3 w-3" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={handlePlayPause}
                                    className="h-8 w-8"
                                >
                                    {isPlaying ? (
                                        <Pause className="h-3 w-3" />
                                    ) : (
                                        <Play className="h-3 w-3" />
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={handleStepForward}
                                    disabled={currentYear >= maxYear}
                                    className="h-8 w-8"
                                >
                                    <SkipForward className="h-3 w-3" />
                                </Button>
                            </div>

                            <div className="flex items-center gap-2 bg-muted px-3 py-1 rounded-full border border-border/50">
                                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Year</span>
                                <span className="text-xs font-bold text-foreground min-w-[60px] text-center">
                                    {formattedCurrentYear}
                                </span>
                            </div>

                            {/* Mobile Era Toggle */}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowErasLegend(!showErasLegend)}
                                className="md:hidden h-8 text-[9px] uppercase tracking-widest text-muted-foreground font-medium gap-1 px-2 border border-border/30"
                            >
                                <Info className="w-3 h-3" />
                                {showErasLegend ? 'Hide Eras' : 'Eras'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom Row: Date and Toggle */}
            <div className="flex items-center justify-center relative min-h-[40px]">
                {/* Passage date - centered */}
                <div className="translate-y-[12px]">
                    <PassageDateDisplay
                        passageDate={passageDate}
                        alternativeDates={alternativeDates}
                    />
                </div>

                {/* Toggle Button - Bottom Right */}
                <div className="absolute right-0 bottom-0 top-0 flex items-center">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDetails(!showDetails)}
                        className="h-8 text-[10px] uppercase tracking-widest text-primary font-bold hover:bg-primary/5 gap-1.5 md:gap-2 px-2"
                    >
                        <SlidersHorizontal className="w-3 h-3" />
                        <span className="hidden sm:inline">{showDetails ? 'Hide Timeline' : 'Show Timeline'}</span>
                        <span className="sm:hidden">{showDetails ? 'Hide' : 'Show'}</span>
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Helper function to extract eras from historical contexts
export function extractErasFromContexts(contexts: HistoricalContext[]): TimelineEra[] {
    const eraColors = [
        '#3b82f6', // blue
        '#8b5cf6', // purple
        '#ec4899', // pink
        '#f59e0b', // amber
        '#10b981', // green
        '#06b6d4', // cyan
        '#f97316', // orange
        '#6366f1', // indigo
    ];

    const uniqueEras = new Map<string, TimelineEra>();

    contexts.forEach((context) => {
        if (!uniqueEras.has(context.period)) {
            uniqueEras.set(context.period, {
                label: context.period,
                startYear: context.sortableYearStart,
                endYear: context.sortableYearEnd,
                color: eraColors[uniqueEras.size % eraColors.length]
            });
        }
    });

    return Array.from(uniqueEras.values()).sort((a, b) => a.startYear - b.startYear);
}
